import React from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MarkdownRenderer from '../../utils/markDownRenderer.';

interface JsonMetadataTableProps {
  jsonMetadata: any;
  entityType: 'licitaciones' | 'compras-agiles';
  resumen: string;
  resumen_llm: string;
  resumen_archivos_llm: string; 
}

const formatKey = (key: string): string => {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

const JsonMetadataTable: React.FC<JsonMetadataTableProps> = ({ jsonMetadata, entityType, resumen, resumen_llm, resumen_archivos_llm }) => {
  if (!jsonMetadata) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" color="text.secondary">
          No hay datos de metadata disponibles
        </Typography>
      </Box>
    );
  }



  const shouldShowValue = (value: any): boolean => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    if (typeof value === 'string' && value.trim() === ' ') return false;
    if (typeof value === 'object' && !Array.isArray(value)) {
      return Object.values(value).some(val => shouldShowValue(val));
    }
    return true;
  };

  const renderValue = (value: any): React.ReactNode => {
    if (typeof value === 'boolean') {
      return value ? (
        <CheckCircleIcon color="success" />
      ) : (
        <CancelIcon color="error" />
      );
    }
    
    if (typeof value === 'string' && (
      value.includes('-') || 
      value.includes('/') || 
      value.includes('T') ||
      /^\d{4}-\d{2}-\d{2}/.test(value) ||
      /^\d{2}\/\d{2}\/\d{4}/.test(value)
    )) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('es-CL', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          });
        }
      } catch (e) {
      }
    }
    
    if (Array.isArray(value)) {
      return (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2">
              {value.length} elemento{value.length !== 1 ? 's' : ''}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ 
              display: { xs: 'block', md: 'none' },
              '& > *:not(:last-child)': { mb: 2 }
            }}>
              {value.map((item, index) => (
                <Paper key={index} sx={{ p: 2, mb: 1 }}>
                  <Typography variant="caption" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                    Elemento {index + 1}
                  </Typography>
                  {Object.entries(item).map(([key, val]) => (
                    <Box key={key} sx={{ mb: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.7rem' }}>
                        {formatKey(key)}:
                      </Typography>
                      <Typography variant="body2" component="div">
                        {typeof val === 'object' && val !== null ? (
                          <Box>
                            {Object.entries(val).map(([objKey, objVal]) => (
                              <Typography key={objKey} variant="body2" sx={{ mb: 0.5, ml: 1 }}>
                                <strong>{formatKey(objKey)}:</strong> {String(objVal)}
                              </Typography>
                            ))}
                          </Box>
                        ) : val === null ? (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            No especificado
                          </Typography>
                        ) : (
                          String(val)
                        )}
                      </Typography>
                    </Box>
                  ))}
                </Paper>
              ))}
            </Box>
            
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {Object.keys(value[0] || {}).map(key => (
                        <TableCell key={key} sx={{ fontWeight: 600 }}>
                          {formatKey(key)}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {value.map((item, index) => (
                      <TableRow key={index}>
                        {Object.entries(item).map(([key, val]) => (
                                                  <TableCell key={key}>
                          {typeof val === 'object' && val !== null ? (
                            <Box>
                              {Object.entries(val).map(([objKey, objVal]) => (
                                <Typography key={objKey} variant="body2" sx={{ mb: 0.5 }}>
                                  <strong>{formatKey(objKey)}:</strong> {String(objVal)}
                                </Typography>
                              ))}
                            </Box>
                          ) : val === null ? (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              No especificado
                            </Typography>
                          ) : (
                            String(val)
                          )}
                        </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </AccordionDetails>
        </Accordion>
      );
    }
    
    if (typeof value === 'object' && value !== null) {
      return (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2">Ver detalles</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <JsonMetadataTable jsonMetadata={value} entityType={entityType} resumen={resumen} resumen_llm={resumen_llm} resumen_archivos_llm={resumen_archivos_llm} />
          </AccordionDetails>
        </Accordion>
      );
    }
    
    return String(value);
  };

  const processObject = (obj: any, prefix = ''): { key: string; value: any; shouldShow: boolean }[] => {
    const result: { key: string; value: any; shouldShow: boolean }[] = [];
    
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        const formattedKey = prefix ? `${formatKey(prefix)} - ${formatKey(key)}` : formatKey(key);
        
        if (value === null || value === undefined) {
          continue;
        }
        
        if (typeof value === 'object' && !Array.isArray(value)) {
          result.push(...processObject(value, key));
        } else {
          const shouldShow = shouldShowValue(value);
          result.push({
            key: formattedKey,
            value,
            shouldShow
          });
        }
      }
    }
    
    return result;
  };

  const processedData = processObject(jsonMetadata).filter(item => item.shouldShow);

  if (processedData.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" color="text.secondary">
          No hay datos válidos en la metadata
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
        Datos completos de {entityType === 'licitaciones' ? 'la licitación' : 'la compra ágil'}
      </Typography>
      {resumen && (
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Resumen de mercado público: <MarkdownRenderer content={resumen} />
        </Typography>
      )}
      {resumen_llm && (
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Resumen LLM: <MarkdownRenderer content={resumen_llm} />
        </Typography>
      )}
      {resumen_archivos_llm && (
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Resumen Archivos LLM: <MarkdownRenderer content={resumen_archivos_llm} />
        </Typography>
      )}
      <Box sx={{ display: { md: 'block', lg: 'none' } }}>
        {processedData.map((item, index) => (
          <Paper key={index} sx={{ p: 2, mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
              {item.key}
            </Typography>
            <Box sx={{ ml: 1 }}>
              {renderValue(item.value)}
            </Box>
          </Paper>
        ))}
      </Box>

      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>
                  Campo
                </TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>
                  Valor
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {processedData.map((item, index) => (
                <TableRow key={index} hover>
                  <TableCell 
                    sx={{ 
                      fontWeight: 500, 
                      fontSize: '0.875rem',
                      borderRight: '1px solid #e0e0e0',
                      minWidth: '200px'
                    }}
                  >
                    {item.key}
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontSize: '0.875rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}
                  >
                    {renderValue(item.value)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default JsonMetadataTable; 